import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plug, RefreshCw } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TESTIDS } from "@/constants/testIds";
import StatusPill from "@/components/StatusPill";

import OverviewTab from "@/pages/project/OverviewTab";
import KeywordsTab from "@/pages/project/KeywordsTab";
import ThreadsTab from "@/pages/project/ThreadsTab";
import PersonasTab from "@/pages/project/PersonasTab";
import PublishingTab from "@/pages/project/PublishingTab";
import OffersTab from "@/pages/project/OffersTab";
import LogsTab from "@/pages/project/LogsTab";
import SitemapsTab from "@/pages/project/SitemapsTab";
import ModerationTab from "@/pages/project/ModerationTab";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [testing, setTesting] = useState(false);

  async function refresh() {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error("Failed to load project");
    }
  }

  useEffect(() => { refresh(); }, [id]);

  async function testWP() {
    setTesting(true);
    try {
      const { data } = await api.post(`/projects/${id}/test-wp`);
      if (data.ok) toast.success(`WP connection OK (user id ${data.wp_user?.id})`);
      else toast.error(`WP connection failed: ${data.error || data.status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "WP test failed");
    } finally { setTesting(false); }
  }

  if (!project) return <div className="p-10 text-muted-foreground text-sm">Loading project…</div>;
  const s = project.stats || {};

  return (
    <div className="px-6 lg:px-10 pt-6 lg:pt-10 pb-20 max-w-[1500px]">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ChevronLeft className="w-3 h-3" /> All projects
      </Link>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight">{project.name}</h1>
            <StatusPill status={project.status} />
            <span className="text-[10px] txt-mono uppercase border border-border px-2 py-0.5 rounded-sm text-muted-foreground">{project.pack_slug}</span>
          </div>
          <div className="text-sm text-muted-foreground txt-mono mt-1">{project.domain || "no domain"}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={TESTIDS.testWpBtn}
            onClick={testWP} disabled={testing || !project.wp_configured}
            className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 rounded-sm transition-colors"
          >
            <Plug className={`w-3 h-3 ${testing ? "animate-pulse" : ""}`} /> Test WP
          </button>
          <button onClick={refresh} className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-border hover:border-primary rounded-sm transition-colors">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </header>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-6 mb-6 border border-border">
        {[
          ["Keywords", s.keywords],
          ["Personas", s.personas],
          ["Threads", s.threads_total],
          ["Published", s.threads_published],
          ["Queue", s.publish_queue],
          ["Clicks", s.clicks],
        ].map(([label, value], i) => (
          <div key={label} className={`p-3 ${i ? "border-l border-border" : ""}`}>
            <div className="text-[10px] txt-mono uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="stat-num text-xl mt-1">{value ?? 0}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 sm:grid-cols-9 bg-transparent border border-border rounded-none p-0 h-auto">
          <TabsTrigger data-testid={TESTIDS.projectTabsOverview} value="overview" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Overview</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsKeywords} value="keywords" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Keywords</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsThreads} value="threads" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Threads</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsPersonas} value="personas" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Personas</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsPublishing} value="publishing" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Publishing</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsOffers} value="offers" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Offers</TabsTrigger>
          <TabsTrigger data-testid="project-tab-moderation" value="moderation" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Moderation</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsLogs} value="logs" className="rounded-none border-r border-border last:border-r-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Logs</TabsTrigger>
          <TabsTrigger data-testid={TESTIDS.projectTabsSitemaps} value="sitemaps" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 text-xs txt-mono uppercase tracking-wider">Sitemaps</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="overview"><OverviewTab project={project} onRefresh={refresh} /></TabsContent>
          <TabsContent value="keywords"><KeywordsTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="threads"><ThreadsTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="personas"><PersonasTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="publishing"><PublishingTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="offers"><OffersTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="moderation"><ModerationTab projectId={id} onRefresh={refresh} /></TabsContent>
          <TabsContent value="logs"><LogsTab projectId={id} /></TabsContent>
          <TabsContent value="sitemaps"><SitemapsTab projectId={id} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
